import { Quote, Star, Heart, ShieldCheck, Sparkles } from "lucide-react";
import useSeo, { PROD_DOMAIN } from "../lib/seo";
import {
  TrustPage, TrustHero, TrustSection, ClosingCta,
} from "../components/TrustLayout";

/**
 * /stories — realistic social proof. Calm, consistent wins. NO before/after
 * weight numbers, NO disease cures, NO hype.
 */

// Editorial-style stories. Names changed, used with permission, quietly.
const STORIES = [
  {
    name: "Aisha M.",
    age: 41,
    city: "London",
    headline: "I slept through the night for the first time in two years.",
    body: "I started Feel Greats because my GP kept telling me I was 'fine' but I felt anything but. Took the assessment on a Sunday evening. The mentor messaged me on Tuesday — no sales pitch, just asked about my cortisol and sleep window. I started with one product and a 9pm screen-off habit. Six weeks in, I'm sleeping through the night. That alone has changed my life.",
    tag: "Sleep · Stress",
    glanceLabel: "What changed",
    glance: "Slept through 5 of 7 nights at week 6.",
    productHint: "Started with: Bios Life E",
  },
  {
    name: "Daniel R.",
    age: 38,
    city: "Manchester",
    headline: "Less belly bloat. Way less. My jeans finally fit again.",
    body: "I was sceptical — I've tried 'gut reset' programmes that cost £400 and did nothing. The mentor here told me my issue was probably fibre, not a fancy protocol. I added Balance to my morning routine, drank more water, and within a month my bloating dropped from 'every meal' to maybe twice a week. Jeans I'd given up on fit again. No dramatic transformation — just quietly better.",
    tag: "Gut · Energy",
    glanceLabel: "What changed",
    glance: "Bloating frequency dropped ~70% by week 4.",
    productHint: "Started with: Balance Fiber Matrix",
  },
  {
    name: "Priya K.",
    age: 46,
    city: "Edinburgh",
    headline: "Peri-menopause felt manageable for the first time.",
    body: "I'd convinced myself I was just 'tired' until the hot flushes started. My GP wasn't ready to prescribe HRT yet and I felt stuck. The mentor at Feel Greats was the first person who didn't make me feel like I was overreacting. We started slow — one product, magnesium-rich foods, and a walk after lunch. Three months in I'm not symptom-free, but I'm functional again. I cried reading their week-6 check-in.",
    tag: "Hormonal balance · Peri-menopause",
    glanceLabel: "What changed",
    glance: "Hot-flush severity halved by month 3.",
    productHint: "Started with: Balanced Woman",
  },
  {
    name: "Sam T.",
    age: 33,
    city: "Bristol",
    headline: "I work later than I should — Unimate kept me focused without the crash.",
    body: "I've been a coffee-after-3pm guy my whole career and I knew it was wrecking my sleep. Switched my afternoon coffee for Unimate two weeks in. The first week was rough (caffeine withdrawal is real), but by week 3 my focus from 3-6pm was actually better than with coffee, and I was asleep before 11pm. Not magic — just a better swap.",
    tag: "Focus · Calm energy",
    glanceLabel: "What changed",
    glance: "Asleep before 11pm 5+ nights/week within 3 weeks.",
    productHint: "Started with: Unimate",
  },
  {
    name: "Marie L.",
    age: 52,
    city: "Newcastle",
    headline: "My energy is back. My family noticed before I did.",
    body: "I didn't tell my husband I was starting anything. About a month in he said, 'You're different. You're laughing at things again.' That was the moment I knew it was working. The mentor here treated me like a person, not a customer. They reminded me to focus on small things — water, walks, the right protein — not just the supplement. I rebooked my year on it.",
    tag: "Energy · Mood",
    glanceLabel: "What changed",
    glance: "Husband-noticed mood shift in week 4.",
    productHint: "Started with: Feel Great System",
  },
  {
    name: "Olu A.",
    age: 29,
    city: "Birmingham",
    headline: "I almost didn't buy. The mentor talked me out of three products.",
    body: "I came in convinced I needed everything. The mentor gently explained that my main issue (anxiety + screen-time) was a habit problem, not a supplement one. She suggested I start with the assessment, a wind-down routine, and just Unimate for the afternoon. I felt seen for the first time in a wellness conversation. I've been a member for six months, and I've only bought one thing.",
    tag: "Stress · Calm",
    glanceLabel: "What changed",
    glance: "Anxiety check-ins (1-10) dropped from 7 to 4 average.",
    productHint: "Started with: Unimate (only)",
  },
];

export default function StoriesPage() {
  useSeo({
    title: "Member stories — Feel Greats real wellness journeys",
    description:
      "Honest, calm, realistic member stories. No before-and-after weight numbers, no hype — just small, consistent shifts in sleep, energy, gut, and hormonal balance.",
    canonical: `${PROD_DOMAIN}/stories`,
    ogType: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": "Feel Greats member stories",
        "url": `${PROD_DOMAIN}/stories`,
        "itemListElement": STORIES.map((s, idx) => ({
          "@type": "ListItem",
          "position": idx + 1,
          "item": {
            "@type": "Review",
            "author": { "@type": "Person", "name": s.name },
            "reviewBody": s.body,
            "name": s.headline,
            "itemReviewed": { "@type": "Organization", "name": "Feel Greats", "url": PROD_DOMAIN },
          },
        })),
      },
    ],
  });

  return (
    <TrustPage testid="stories-page">
      <TrustHero
        eyebrow="Member stories"
        headline="Quiet, consistent wins."
        highlightWord="Quiet"
        body="We don't share transformation photos. We don't print weight numbers. We share what our members actually experience — small, real shifts in sleep, energy, gut, mood and hormonal balance. Names used with permission."
        primaryCta={{ to: "/questionnaire", label: "Start your own quiet shift", testid: "stories-hero-cta", icon: Sparkles }}
        testid="stories-hero"
      />

      {/* Stories grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-semibold">Six honest stories</p>
            <h2 className="font-display text-3xl sm:text-4xl text-emerald-950 font-light leading-tight mt-2 max-w-3xl">
              The kind of wins we celebrate.
            </h2>
          </div>
          <p className="text-[12px] text-emerald-900/55 italic max-w-sm">
            Results vary. We always note the product, the timeframe, and the small habit changes that supported each story.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="stories-grid">
          {STORIES.map((s, idx) => <StoryCard key={s.name + idx} story={s} idx={idx} />)}
        </div>
      </section>

      <TrustSection
        eyebrow="What we don't do"
        title="No fake urgency. No fake transformations."
        body="If a wellness brand uses dramatic before/after photos, scarcity timers, or 'guaranteed results in 30 days' language — they're selling fear, not health. We try to be the calm opposite."
        tone="amber"
        testid="stories-honesty"
      />

      <ClosingCta
        eyebrow="Your story is next"
        title="Curious what your wins might look like?"
        body="The assessment is two minutes, the mentor message is one tap, and the first month is usually about settling — not transformations. Take it slow with us."
      />
    </TrustPage>
  );
}

function StoryCard({ story, idx }) {
  return (
    <article
      className="rounded-3xl border border-emerald-100 bg-white p-6 sm:p-8 hover:border-emerald-300 hover:shadow-sm transition flex flex-col"
      data-testid={`story-card-${idx}`}
    >
      <Quote className="w-7 h-7 text-emerald-700/40" strokeWidth={1.4} />
      <h3 className="font-display text-xl sm:text-2xl text-emerald-950 font-light leading-snug mt-3">
        "{story.headline}"
      </h3>
      <p className="mt-4 text-[14.5px] text-emerald-900/85 leading-relaxed flex-1">
        {story.body}
      </p>

      <div className="mt-5 rounded-2xl bg-emerald-50/60 border border-emerald-100 p-3 text-[12.5px] text-emerald-900">
        <div className="flex items-start gap-2">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-700 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold uppercase tracking-wider text-[10px] text-emerald-700">{story.glanceLabel}</p>
            <p className="mt-0.5">{story.glance}</p>
          </div>
        </div>
      </div>

      <footer className="mt-5 flex items-end justify-between flex-wrap gap-2">
        <div>
          <p className="font-semibold text-emerald-950 text-sm">{story.name}, {story.age}</p>
          <p className="text-[11px] text-emerald-900/55 mt-0.5">{story.city} · {story.tag}</p>
        </div>
        <p className="text-[11px] text-emerald-700/80 inline-flex items-center bg-emerald-50/80 rounded-full px-2.5 py-0.5">
          <Heart className="w-3 h-3 mr-1" /> {story.productHint}
        </p>
      </footer>
    </article>
  );
}
